import config from '@iotv/config'
import { DynamicIndexObjectType, MapLike, ValidBusinessObject } from '@iotv/iotv-v3-types'
import { SYSTEM } from '../../constants'
import { UserAttributeDefs } from '../../types/ModelTypes'
import { getAcceptableDynamoDBKey } from '../../util/objects/identity'
import { getNiceDate } from '../../util/time/millisecondOperations'
import { ModelCommon } from '../ModelCommon'
import { AlertTrigger } from './AlertTrigger'
import { AlertIncidentType, Severity } from './enums'
import {
  getIncidentDSK,
  IIncident,
  IncidentDSKParams,
  IncidentEvent,
  IncidentHistoryRecord,
  IncidentStatus,
  NotificationRecord,
  NotificationRecords,
} from './IncidentTypes'

const debug = !process.env.AWS_SESSION_TOKEN && false

const typeName = 'Incident'

export class Incident extends ModelCommon implements IIncident, DynamicIndexObjectType {
  constructor({ _status, _severity, ...ob }: MapLike<any>) {
    super(ob.type || typeName, { ...ob })
    this._status = _status
    this._severity = _severity
    this.updateDynamicKeys()
  }

  dpk?: string
  dsk?: string
  _severity?: Severity
  _status?: IncidentStatus
  _incidentEntity?: ValidBusinessObject
  _initiator?: ValidBusinessObject
  _incidentType?: AlertIncidentType
  _incidentSubtype?: string
  _summary?: string | undefined
  ttl: number | undefined
  timestamp: number | undefined
  closedTimestamp: number | undefined
  history?: IncidentHistoryRecord[]
  notificationRecords?: NotificationRecords

  get summary(): string | undefined {
    return this._summary
  }

  set summary(summary: string | undefined) {
    this._summary = summary
  }

  addToHistory(historyRecord: IncidentHistoryRecord) {
    if (!this.history) {
      this.history = []
    }
    this.history.unshift(historyRecord)
    let i = this.history?.length ?? 0

    //purge when lots of events
    while (this.history?.length > 25) {
      const idx = i - 1
      const historyEvent = this.history[i] && this.history[i].event
      if (!historyEvent || ![IncidentEvent.OPENED, IncidentEvent.CLOSED].includes(historyEvent as IncidentEvent)) {
        this.history.pop()
      }
      i--
    }
  }

  addNotificationRecord(notificationRecord: NotificationRecord) {
    if (!this.notificationRecords) {
      this.notificationRecords = {}
    }
    const acceptableDynamoDBKey = getAcceptableDynamoDBKey(notificationRecord.recipient.sk)
    if (!this.notificationRecords[acceptableDynamoDBKey]) {
      this.notificationRecords[acceptableDynamoDBKey] = []
    }

    this.notificationRecords[acceptableDynamoDBKey].push(notificationRecord)
  }

  openIncident(initiator: ValidBusinessObject = SYSTEM) {
    debug && console.log('Setting status open')
    this.status = IncidentStatus.OPEN
    debug && console.log('status', this.status)
    const historyRecord: IncidentHistoryRecord = {
      timestamp: Date.now(),
      initiator,
      event: IncidentEvent.OPENED,
    }
    this.addToHistory(historyRecord)
    this.updateDynamicKeys()
  }

  closeIncident(initiator: ValidBusinessObject) {
    this.status = IncidentStatus.CLOSED
    const historyRecord: IncidentHistoryRecord = {
      timestamp: Date.now(),
      initiator,
      event: IncidentEvent.CLOSED,
    }
    this.addToHistory(historyRecord)
    this.updateDynamicKeys()
  }

  // returns ghostified Incident for use when need correct dsk dpk of an incident for an alert trigger
  getIncidentZombieForAlert<T>(alertTrigger: AlertTrigger<T>) {
    let derivedIncident: (IIncident & ValidBusinessObject) | undefined = undefined
    const incidentEntity = alertTrigger.entityTriggeringAlert

    const incidentType =
      alertTrigger.alertTriggerType && ((AlertIncidentType[alertTrigger.alertTriggerType] ?? AlertIncidentType.SYSTEM_ISSUE) as AlertIncidentType)
    const { initiator, severity, status, summary, alertTriggerSubtype: incidentSubtype } = alertTrigger
    if (initiator && incidentEntity && incidentType) {
      debug && console.log('getting dsk from initiator, severity, status, summary, alertTriggerSubtype, on alertTrigger ', alertTrigger)
      const derivedIncidentId = getIncidentDSK({
        initiator,
        incidentEntity,
        incidentType,
        incidentSubtype,
      })
      debug && console.log('derived dsk is ', derivedIncidentId)
      derivedIncident = {
        sk: config.app.ghostUuid,
        pk: incidentEntity.sk,
        dsk: derivedIncidentId,
        dpk: typeName,
        type: typeName,
        id: derivedIncidentId,
        _initiator: initiator,
        _incidentEntity: incidentEntity,
        _incidentType: incidentType,
        _incidentSubtype: incidentSubtype,
        _severity: severity,
        _status: status,
        _summary: summary,
        closedTimestamp: undefined,
        timestamp: undefined,
        ttl: undefined,
      } as (IIncident & ValidBusinessObject) | undefined
    } else {
      debug && console.log('No initiator, severity, status, on alertTrigger', alertTrigger)
    }
    debug && console.log('returning derivedIncident', derivedIncident)

    return derivedIncident
  }

  recordAlertTrigger<T>(alertTrigger: AlertTrigger<T>) {
    const historyRecord: IncidentHistoryRecord = {
      initiator: alertTrigger.initiator ?? SYSTEM,
      timestamp: Date.now(),
      event: alertTrigger.alertTriggerType ?? IncidentEvent.UPDATED,
      summary: alertTrigger.summary,
      alertSk: alertTrigger.sk,
    }
    this.addToHistory(historyRecord)
    this.updateDynamicKeys()
  }

  updateDynamicKeys() {
    const { incidentEntity, incidentType, incidentSubtype, initiator }: Partial<IncidentDSKParams> = this
    if (incidentEntity && incidentType && initiator) {
      this.dsk = getIncidentDSK({
        incidentEntity,
        incidentType,
        incidentSubtype,
        initiator,
      })
    } else {
      debug &&
        console.log('WARN updateDynamicKeys something missing', {
          incidentEntity,
          incidentType,
          initiator,
        })
    }

    this.dpk = this.type
  }

  get incidentEntity() {
    return this._incidentEntity
  }

  set incidentEntity(entity: ValidBusinessObject | undefined) {
    this._incidentEntity = entity
  }

  get incidentEntityName() {
    return this._incidentEntity?.name
  }

  set incidentEntityName(name: string | undefined) {
    if (this._incidentEntity) this._incidentEntity.name = name
  }

  get status() {
    return this._status
  }
  set status(newStatus: IncidentStatus | undefined) {
    this._status = newStatus
    this.updateDynamicKeys()
  }

  get severity() {
    return this._severity
  }
  set severity(newSeverity: Severity | undefined) {
    this._severity = newSeverity
    this.updateDynamicKeys()
  }

  get initiator() {
    return this._initiator
  }

  set initiator(initiator: ValidBusinessObject | undefined) {
    this._initiator = initiator
  }

  get incidentType() {
    return this._incidentType
  }

  set incidentType(incidentType: AlertIncidentType | undefined) {
    this._incidentType = incidentType
  }

  get incidentSubtype() {
    return this._incidentSubtype
  }

  set incidentSubtype(incidentSubtype: string | undefined) {
    this._incidentSubtype = incidentSubtype
  }

  /** timestamp is string in dynamodb main-table */
  get timestampOpen() {
    return this.timestamp ?? undefined
  }

  set timestampOpen(timestamp: number | undefined) {
    this.timestamp = timestamp
  }

  getUserAttributes(): UserAttributeDefs {
    const atts: UserAttributeDefs = {
      ...super.getUserAttributes(),
      _incidentType: {
        type: 'Enum',
        label: 'Incident Type',
        editable: false,
        enumKv: AlertIncidentType,
        displayFn: (v: AlertIncidentType) => v.slice(0, 1) + v.slice(1).toLowerCase().replaceAll('_', ' '),
      },
      _severity: {
        type: 'Enum',
        label: 'Severity',
        editable: true,
        enumKv: Severity,
        displayFn: (v: Severity) => v.slice(0, 1) + v.slice(1).toLowerCase().replaceAll('_', ' '),
      },
      _status: {
        type: 'Enum',
        label: 'Status',
        editable: true,
        enumKv: IncidentStatus,
        displayFn: (v: IncidentStatus) => v.slice(0, 1) + v.slice(1).toLowerCase().replaceAll('_', ' '),
      },
      timestamp: { type: 'Number', editable: false, displayFn: (v: number) => getNiceDate(v) },
    }

    delete atts.description
    delete atts.type

    if (this.status === IncidentStatus.CLOSED) {
      atts.closedTimestamp = {
        type: 'Date',
        label: 'Closed at',
        editable: false,
      }
    }

    return atts
  }
}
